/**
 * Head/SEO: setzt <title>, meta description, canonical, Open Graph und JSON-LD
 * via react-helmet-async. Auf jeder öffentlichen Seite verwendbar.
 */
import React from "react";
import { Helmet } from "react-helmet-async";
import { imgSet } from "@/lib/img";

const CANONICAL_HOST = "https://fotoboxverkauf.de";
const DEFAULT_OG = `${CANONICAL_HOST}/og-default.jpg`;
const OG_LOCALE = { de: "de_DE", en: "en_GB", nl: "nl_NL" };
const DEFAULT_TITLE = {
  de: "fotoboxverkauf.de: Professionelle Fotoboxen für Gewerbe",
  en: "fotoboxverkauf.de: Professional photo booths for businesses",
  nl: "fotoboxverkauf.de: Professionele fotoboxen voor bedrijven",
};
const DEFAULT_DESC = {
  de: "Professionelle Fotoboxen, Magic Mirror, 360°-Systeme mit Sofortdruck, deutschlandweite Lieferung von fotoboxverkauf.de.",
  nl: "Professionele fotoboxen, magic mirrors en 360° videobooths met directe afdruk, levering in Nederland en België door fotoboxverkauf.de.",
};

export default function Seo({
  title,
  description,
  path = "",
  image = DEFAULT_OG,
  type = "website",
  noindex = false,
  jsonLd = null, // Objekt oder Array von Objekten
  lang = "de", // "en" bzw. "nl" für die Sprachbereiche /en und /nl
  alternates = null, // Sprachvarianten { de: "/x", en: "/en/y", nl: "/nl/z" } → hreflang (+ x-default = de)
  preloadImage = null, // Hauptbild der Seite (LCP): { src, sizes } oder URL; wird im statischen Seitenkopf vorgeladen
}) {
  // Regel Inhaber 28.10.2026: keine Gedankenstriche in Texten, auch nicht in Google-Titeln und -Beschreibungen
  const noDash = (t, sep) => String(t || "").replace(/\s+[–—]\s+/g, sep).replace(/\s*:\s*fotoboxverkauf\.de$/, "");
  const fullTitle = title ? `${noDash(title, ": ")} · fotoboxverkauf.de` : (DEFAULT_TITLE[lang] || DEFAULT_TITLE.de);
  const desc = noDash(description || DEFAULT_DESC[lang] || DEFAULT_DESC.de, ", ");
  const canonical = `${CANONICAL_HOST}${path || (typeof window !== "undefined" ? window.location.pathname : "/")}`;
  const jsonBlocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const pre = preloadImage ? (typeof preloadImage === "string" ? { src: preloadImage } : preloadImage) : null;
  const alt = alternates
    ? [...Object.entries(alternates).filter(([hl, p]) => p && hl !== "x-default"),
       ...((alternates["x-default"] || alternates.de) ? [["x-default", alternates["x-default"] || alternates.de]] : [])]
    : [];
  const preSet = pre?.src ? imgSet(pre.src, pre.sizes || "(max-width: 768px) 100vw, 50vw") : null;

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc}/>
      <link rel="canonical" href={canonical}/>
      {noindex && <meta name="robots" content="noindex, follow"/>}
      {/* data-rh ausdrücklich: React 19 rendert die Tags selbst (ohne Helmet-Markierung); deploy/prerender_heads.js liest sie so aus */}
      {alt.map(([hl, p]) => <link key={hl} rel="alternate" hrefLang={hl} href={`${CANONICAL_HOST}${p}`} data-rh="true"/>)}
      <meta property="og:title" content={fullTitle}/>
      <meta property="og:description" content={desc}/>
      <meta property="og:type" content={type}/>
      <meta property="og:url" content={canonical}/>
      <meta property="og:image" content={image}/>
      <meta property="og:site_name" content="fotoboxverkauf.de"/>
      <meta property="og:locale" content={OG_LOCALE[lang] || OG_LOCALE.de}/>
      <meta name="twitter:card" content="summary_large_image"/>
      <meta name="twitter:title" content={fullTitle}/>
      <meta name="twitter:description" content={desc}/>
      <meta name="twitter:image" content={image}/>
      {preSet && (
        <link rel="preload" as="image" href={preSet.src} imageSrcSet={preSet.srcSet} imageSizes={preSet.sizes} fetchpriority="high" />
      )}
      {jsonBlocks.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}

/** Hilfs-Builder für BreadcrumbList JSON-LD */
export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${CANONICAL_HOST}${it.path}`,
    })),
  };
}
